import * as React from 'react';

export interface PhoneSystemHealth {
  backend_healthy: boolean | null;
  restart_enabled: boolean;
  restart_running: boolean;
  last_restart_at: number | null;
  last_restart_result: string | null;
}

const PhoneSystemContext = React.createContext<PhoneSystemHealth | null>(null);

/** The host proxy stays available while the realtime backend restarts. */
export function PhoneSystemProvider({ children }: { children: React.ReactNode }) {
  const [health, setHealth] = React.useState<PhoneSystemHealth | null>(null);

  React.useEffect(() => {
    let active = true;
    let inFlight = false;

    const check = async () => {
      if (inFlight) return;
      inFlight = true;
      const controller = new AbortController();
      const deadline = window.setTimeout(() => controller.abort(), 4_000);
      try {
        const response = await fetch('/proxy/health', {
          headers: { Accept: 'application/json' },
          cache: 'no-store',
          credentials: 'same-origin',
          signal: controller.signal,
        });
        if (!response.ok) throw new Error('Proxy health unavailable');
        const next = (await response.json()) as PhoneSystemHealth;
        if (active) setHealth(next);
      } catch {
        if (active) setHealth(null);
      } finally {
        window.clearTimeout(deadline);
        inFlight = false;
      }
    };

    void check();
    const timer = window.setInterval(() => void check(), 5_000);
    return () => {
      active = false;
      window.clearInterval(timer);
    };
  }, []);

  return <PhoneSystemContext.Provider value={health}>{children}</PhoneSystemContext.Provider>;
}

export function usePhoneSystemHealth(): PhoneSystemHealth | null {
  return React.useContext(PhoneSystemContext);
}
